/**
 * StoredCorrelationSummaryCard - Displays correlation summaries from correlation_insights_v2
 * Available to ALL users (Standard and Premium)
 * 
 * This component is purely UI - it displays pre-computed, stored insights
 * It does NOT perform any new AI reasoning or interpretation
 * All text shown is already stored in the database
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, Info, Sparkles } from 'lucide-react';
import { 
  useStoredCorrelationSummaries, 
  formatMetricNameForDisplay,
  getCorrelationCategoryLabel,
  type StoredCorrelationSummary 
} from '@/hooks/useStoredCorrelationSummaries';
import { format, parseISO } from 'date-fns';

interface StoredCorrelationSummaryCardProps {
  days?: number;
  maxItems?: number;
}

export function StoredCorrelationSummaryCard({ 
  days = 30,
  maxItems = 5 
}: StoredCorrelationSummaryCardProps) {
  const { data: summaries, isLoading } = useStoredCorrelationSummaries(days);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  const displaySummaries = summaries?.slice(0, maxItems) || [];

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sparkles className="w-5 h-5 text-primary" />
          Your Detected Patterns
        </CardTitle>
        <CardDescription>
          Based on your tracking data from the last {days} days, here are the strongest relationships we've found
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Explanation of what correlations mean */}
        <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground">What does this mean?</strong> A correlation shows when two things tend to change together. 
            For example, if sleep and mood have a "strong positive" correlation, it means when your sleep improves, 
            your mood tends to improve too. This helps you understand which habits have the biggest impact on your wellbeing.
          </p>
        </div>

        {displaySummaries.length === 0 ? (
          <NoPatternMessage />
        ) : (
          <div className="space-y-4">
            {displaySummaries.map((summary) => (
              <CorrelationSummaryItem key={summary.id} summary={summary} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function NoPatternMessage() {
  return (
    <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
      <Info className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
      <div>
        <p className="text-sm text-muted-foreground">
          There is no strong pattern detected in this period.
        </p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          Continue tracking your health metrics consistently to discover meaningful correlations.
        </p>
      </div>
    </div>
  );
}

interface CorrelationSummaryItemProps {
  summary: StoredCorrelationSummary;
}

function CorrelationSummaryItem({ summary }: CorrelationSummaryItemProps) {
  const isPositive = summary.correlation_strength > 0;
  const categoryLabel = getCorrelationCategoryLabel(summary.correlation_strength);
  
  // Format date range for display
  const dateRangeText = `${format(parseISO(summary.date_range_start), 'MMM d')} - ${format(parseISO(summary.date_range_end), 'MMM d')}`;

  return (
    <div className="p-4 rounded-lg border border-border/50 bg-card/50 space-y-3">
      {/* Header with metrics and strength badge */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground">
            {formatMetricNameForDisplay(summary.metric_a)}
          </span>
          <span className="text-muted-foreground">↔</span>
          <span className="text-sm font-medium text-foreground">
            {formatMetricNameForDisplay(summary.metric_b)}
          </span>
        </div>
        <Badge 
          variant={isPositive ? 'default' : 'secondary'}
          className="shrink-0"
        >
          {isPositive ? (
            <TrendingUp className="w-3 h-3 mr-1" />
          ) : (
            <TrendingDown className="w-3 h-3 mr-1" />
          )}
          {categoryLabel}
        </Badge>
      </div>

      {/* Pre-stored insight text - displayed as-is from database */}
      <p className="text-sm text-muted-foreground leading-relaxed">
        {summary.insight_text}
      </p>

      {/* Metadata footer */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground/70">
        <span>{dateRangeText}</span>
        <span>•</span>
        <span>{summary.sample_size} data points</span>
      </div>
    </div>
  );
}
